import { type RefObject, useRef, useState } from 'react'
import { Bookmark, ChevronDown, Pencil, RotateCcw, Save, SavePen, Settings2, Star, Trash2 } from 'lucide-react'

import { cn } from '@/lib/utils'
import { buttonVariants } from '@/ui/button-variants'
import { Button } from '@/ui/button'
import { ConfirmDialog } from '@/ui/confirm-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuGroupLabel,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/ui/dropdown-menu'

import type { DataGridSavedView } from '../api/data-grid-saved-views-api'
import type { DataGridSavedViewsState } from '../lib/use-data-grid-saved-views'
import { keepKeysOutOfMenu } from './data-grid-filter-shared'
import { DataGridSavedViewNameDialog } from './data-grid-saved-view-name-dialog'

const EVERYTHING = 'everything'

export function DataGridSavedViews({ savedViews }: {
  savedViews: DataGridSavedViewsState | undefined
}) {
  if (!savedViews) return null
  return (
    <DataGridSavedViewsMenu
      key={savedViews.scopeKey}
      savedViews={savedViews}
      presentation="standalone"
    />
  )
}

export function DataGridSavedViewsSubmenu({
  savedViews,
  onOpenDialog,
  parentMenuTriggerRef,
}: {
  savedViews: DataGridSavedViewsState | undefined
  onOpenDialog: () => void
  parentMenuTriggerRef: RefObject<HTMLElement | null>
}) {
  if (!savedViews) return null
  return (
    <DataGridSavedViewsMenu
      key={savedViews.scopeKey}
      savedViews={savedViews}
      presentation="submenu"
      onOpenDialog={onOpenDialog}
      parentMenuTriggerRef={parentMenuTriggerRef}
    />
  )
}

function DataGridSavedViewsMenu({
  savedViews,
  presentation,
  onOpenDialog,
  parentMenuTriggerRef,
}: {
  savedViews: DataGridSavedViewsState
  presentation: 'standalone' | 'submenu'
  onOpenDialog?: () => void
  parentMenuTriggerRef?: RefObject<HTMLElement | null>
}) {
  const viewsQuery = savedViews.query
  const createView = savedViews.create
  const renameView = savedViews.rename
  const setDefault = savedViews.setDefault
  const deleteView = savedViews.delete
  const overwriteView = savedViews.overwrite
  const [menuOpen, setMenuOpen] = useState(false)
  const [saveOpen, setSaveOpen] = useState(false)
  const [manageOpen, setManageOpen] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)
  const [viewToRename, setViewToRename] = useState<DataGridSavedView | null>(null)
  const [viewToDelete, setViewToDelete] = useState<DataGridSavedView | null>(null)
  const [overwriteOpen, setOverwriteOpen] = useState(false)
  const [viewToOverwrite, setViewToOverwrite] = useState<DataGridSavedView | null>(null)
  const standaloneTriggerRef = useRef<HTMLButtonElement>(null)
  const finalFocus = parentMenuTriggerRef ?? standaloneTriggerRef
  const views = savedViews.views ?? []
  const canSave = savedViews.canSave
  const saveOverTarget = savedViews.activeView?.state ? null : savedViews.originView
  const activeValue = savedViews.activeView?.savedViewId
    ?? (savedViews.status === 'everything' ? EVERYTHING : '')
  const activeLabel = savedViews.activeView?.name ?? {
    everything: 'Everything',
    custom: 'Custom view',
    loading: 'Loading views…',
    unavailable: 'Views unavailable',
    saved: 'Custom view',
  }[savedViews.status]

  function applyValue(value: string) {
    if (value === EVERYTHING) {
      savedViews.apply(null)
      return
    }
    const selected = views.find((view) => view.savedViewId === value)
    if (selected) savedViews.apply(selected)
  }

  function leaveMenuForDialog() {
    if (presentation === 'standalone') setMenuOpen(false)
    else onOpenDialog?.()
  }

  function changeDeleteOpen(next: boolean) {
    if (!next && !deleteView?.isPending) {
      setViewToDelete(null)
      deleteView?.reset()
    }
  }

  function finishDelete() {
    setViewToDelete(null)
    deleteView?.reset()
  }

  function changeOverwriteOpen(next: boolean) {
    if (!next && !overwriteView.isPending) finishOverwrite()
  }

  function finishOverwrite() {
    setOverwriteOpen(false)
    overwriteView.reset()
  }

  function confirmOverwrite() {
    if (!viewToOverwrite) return
    if (!savedViews.canOverwrite || savedViews.originView?.savedViewId !== viewToOverwrite.savedViewId) {
      finishOverwrite()
      return
    }
    overwriteView.mutate({ savedViewId: viewToOverwrite.savedViewId }, { onSuccess: finishOverwrite })
  }

  const menuItems = (
    <>
      <DropdownMenuRadioGroup
        aria-label="View options"
        value={activeValue}
        onValueChange={applyValue}
      >
        <DropdownMenuRadioItem value={EVERYTHING} indicator="check">
          <RotateCcw aria-hidden className="size-3.5" />
          Everything
        </DropdownMenuRadioItem>
      </DropdownMenuRadioGroup>

      <DropdownMenuSeparator />
      <DropdownMenuGroup>
        <DropdownMenuGroupLabel>My views</DropdownMenuGroupLabel>
        <DropdownMenuRadioGroup
          aria-label="My views"
          value={activeValue}
          onValueChange={applyValue}
        >
          {views.map((view) => (
            <DropdownMenuRadioItem
              key={view.savedViewId}
              value={view.savedViewId}
              indicator="check"
            >
              <Bookmark aria-hidden className="size-3.5" />
              <span className="max-w-40 truncate">{view.name}</span>
              {view.isDefault ? <>{' '}<DefaultMarker /></> : null}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        {viewsQuery.isPending ? (
          <DropdownMenuItem disabled>Loading saved views…</DropdownMenuItem>
        ) : null}
        {viewsQuery.isError ? (
          <DropdownMenuItem onClick={() => void viewsQuery.refetch()}>
            Retry saved views
          </DropdownMenuItem>
        ) : null}
        {!viewsQuery.isPending && !viewsQuery.isError && views.length === 0 ? (
          <DropdownMenuItem disabled>No saved views yet</DropdownMenuItem>
        ) : null}
      </DropdownMenuGroup>

      {createView || (deleteView && views.length > 0)
        ? <DropdownMenuSeparator />
        : null}
      {createView ? <DropdownMenuItem
        disabled={!canSave}
        closeOnClick={presentation === 'standalone'}
        onClick={() => {
          leaveMenuForDialog()
          createView.reset()
          setSaveOpen(true)
        }}
      >
        <Save aria-hidden className="size-3.5" />
        {canSave ? 'Save current view…' : 'Change the table to save a view'}
      </DropdownMenuItem> : null}
      {saveOverTarget ? (
        <DropdownMenuItem
          disabled={!savedViews.canOverwrite}
          closeOnClick={presentation === 'standalone'}
          onClick={() => {
            leaveMenuForDialog()
            overwriteView.reset()
            setViewToOverwrite(saveOverTarget)
            setOverwriteOpen(true)
          }}
        >
          <SavePen aria-hidden className="size-3.5" />
          <span className="max-w-48 truncate">Save over “{saveOverTarget.name}”…</span>
        </DropdownMenuItem>
      ) : null}
      {deleteView && views.length > 0 ? (
        <DropdownMenuItem
          closeOnClick={presentation === 'standalone'}
          onClick={() => {
            leaveMenuForDialog()
            if (!setDefault.isPending) setDefault.reset()
            setManageOpen(true)
          }}
        >
          <Settings2 aria-hidden className="size-3.5" />
          Manage saved views…
        </DropdownMenuItem>
      ) : null}
    </>
  )

  return (
    <>
      {presentation === 'standalone' ? <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger
          ref={standaloneTriggerRef}
          aria-label={`Table view: ${activeLabel}`}
          className={cn(
            buttonVariants({ variant: 'outline', size: 'sm' }),
            'max-w-48 gap-1.5',
          )}
        >
          <Bookmark aria-hidden className="size-3.5 shrink-0" />
          <span className="truncate">{activeLabel}</span>
          <ChevronDown aria-hidden className="text-muted-foreground size-3 shrink-0" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-56">
          {menuItems}
        </DropdownMenuContent>
      </DropdownMenu> : (
        <DropdownMenuSub>
          <DropdownMenuSubTrigger openOnHover>
            <Bookmark aria-hidden className="size-3.5" />
            <span className="min-w-0 flex-1 truncate">Views</span>
            <span className="text-muted-foreground max-w-28 truncate">{activeLabel}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="min-w-56">
            {menuItems}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      )}

      {createView ? <DataGridSavedViewNameDialog
        open={saveOpen}
        title="Save view"
        description="Save the current filters, columns, sort, search, and options."
        initialName=""
        submitLabel="Save view"
        pendingLabel="Saving…"
        nameMaxLength={savedViews.nameMaxLength}
        mutation={createView}
        onSubmit={(name, onDone) => {
          if (canSave) createView.mutate({ name }, { onSuccess: onDone })
        }}
        onClose={() => setSaveOpen(false)}
        finalFocus={finalFocus}
      /> : null}

      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent finalFocus={finalFocus} onKeyDown={keepKeysOutOfMenu}>
          <DialogTitle>Manage saved views</DialogTitle>
          <DialogDescription>
            Rename or delete a personal view, or mark one as your default.
          </DialogDescription>
          <ul className="flex max-h-72 flex-col gap-2 overflow-y-auto">
            {views.map((view) => (
              <li
                key={view.savedViewId}
                className="border-border flex items-center gap-1 rounded-md border py-1 pr-1 pl-3"
              >
                <span className="min-w-0 flex-1 truncate">{view.name}</span>
                {view.isDefault ? <DefaultMarker /> : null}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-pressed={view.isDefault}
                  aria-label={`Use ${view.name} as default`}
                  title={view.isDefault ? 'Remove as default' : 'Use as default'}
                  disabled={setDefault.isPending}
                  focusableWhenDisabled
                  onClick={() => setDefault.mutate({
                    savedViewId: view.savedViewId,
                    isDefault: !view.isDefault,
                  })}
                >
                  <Star aria-hidden className={cn(view.isDefault && 'fill-current')} />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Rename ${view.name}`}
                  title="Rename"
                  onClick={() => {
                    setManageOpen(false)
                    renameView.reset()
                    setViewToRename(view)
                    setRenameOpen(true)
                  }}
                >
                  <Pencil aria-hidden />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Delete ${view.name}`}
                  title="Delete"
                  onClick={() => {
                    setManageOpen(false)
                    deleteView?.reset()
                    setViewToDelete(view)
                  }}
                >
                  <Trash2 aria-hidden />
                </Button>
              </li>
            ))}
          </ul>
          {setDefault.isError ? (
            <p role="alert" className="text-destructive">
              {setDefault.errorMessage(setDefault.error)}
            </p>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setManageOpen(false)}
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DataGridSavedViewNameDialog
        open={renameOpen}
        title="Rename view"
        description="Give this personal view a new name. Its table stays the same."
        initialName={viewToRename?.name ?? ''}
        submitLabel="Rename view"
        pendingLabel="Renaming…"
        nameMaxLength={savedViews.nameMaxLength}
        mutation={renameView}
        onSubmit={(name, onDone) => {
          if (viewToRename) {
            renameView.mutate({ savedViewId: viewToRename.savedViewId, name }, { onSuccess: onDone })
          }
        }}
        onClose={() => setRenameOpen(false)}
        finalFocus={finalFocus}
      />

      <ConfirmDialog
        open={overwriteOpen}
        onOpenChange={changeOverwriteOpen}
        title={`Save over “${viewToOverwrite?.name ?? ''}”?`}
        description={viewToOverwrite ? overwriteDescription(viewToOverwrite) : ''}
        confirmLabel="Save over view"
        pendingLabel="Saving…"
        mutation={overwriteView}
        finalFocus={finalFocus}
        onConfirm={confirmOverwrite}
        errorMessage={overwriteView.errorMessage(overwriteView.error)}
      />

      {deleteView ? <ConfirmDialog
        open={viewToDelete !== null}
        onOpenChange={changeDeleteOpen}
        title={`Delete ${viewToDelete?.name ?? 'saved view'}?`}
        description="The table stays as it is, but this view will no longer appear in your saved views."
        confirmLabel="Delete view"
        pendingLabel="Deleting…"
        mutation={deleteView}
        finalFocus={finalFocus}
        onConfirm={() => {
          if (!viewToDelete) return
          deleteView.mutate(viewToDelete.savedViewId, {
            onSuccess: finishDelete,
          })
        }}
        errorMessage="Couldn’t delete this view. Try again."
      /> : null}
    </>
  )
}

function overwriteDescription(view: DataGridSavedView) {
  const keeps = view.isDefault ? 'It keeps its name and stays your default.' : 'It keeps its name.'
  return `“${view.name}” will show the current filters, columns, sort, search, and options from now on. ${keeps}`
}

function DefaultMarker() {
  return (
    <span className="bg-muted text-foreground rounded-sm px-1.5 text-[10px] font-medium">
      Default
    </span>
  )
}
